import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendingUp, Repeat, Package, Zap, Plus, Pencil, Trash2, Sparkles } from "lucide-react";

const fmt = (n, currency = "EUR") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(n) || 0);

const PACKAGE_LABELS = { incorporate: "Thành lập", setup: "Thiết lập & tuân thủ", turnkey: "Trọn gói" };
const STATUS_LABELS = { active: "Đang chạy", paused: "Tạm dừng", churned: "Đã nghỉ" };

const emptyDeal = {
  client_name: "", client_email: "", company: "", package: "turnkey",
  setup_fee: 9500, monthly_fee: 249, commission_rate: 6, status: "active", notes: "",
};

export default function AdminMetrics() {
  const [metrics, setMetrics] = useState(null);
  const [deals, setDeals] = useState([]);
  const [offer, setOffer] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [m, d, o] = await Promise.all([
        api.get("/admin/metrics"),
        api.get("/admin/commercial-deals"),
        api.get("/platform/turnkey-offer"),
      ]);
      setMetrics(m.data);
      setDeals(d.data.items || []);
      setOffer(o.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Không tải được dữ liệu");
    }
  };
  useEffect(() => { load(); }, []);

  const saveDeal = async () => {
    if (!editing.client_name || !editing.package) {
      toast.error("Nhập tên khách và chọn gói");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...editing,
        setup_fee: Number(editing.setup_fee || 0),
        monthly_fee: Number(editing.monthly_fee || 0),
        commission_rate: Number(editing.commission_rate || 0),
      };
      if (editing.id) {
        await api.patch(`/admin/commercial-deals/${editing.id}`, payload);
        toast.success("Đã cập nhật hợp đồng");
      } else {
        await api.post("/admin/commercial-deals", payload);
        toast.success("Đã tạo hợp đồng mới");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Lỗi khi lưu");
    } finally {
      setBusy(false);
    }
  };

  const deleteDeal = async (id) => {
    if (!window.confirm("Xoá hợp đồng này?")) return;
    try {
      await api.delete(`/admin/commercial-deals/${id}`);
      toast.success("Đã xoá");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const patchOffer = async (upd) => {
    try {
      const { data } = await api.patch("/admin/platform/turnkey-offer", upd);
      setOffer(data);
      toast.success("Đã cập nhật ưu đãi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="admin-metrics">
        <div>
          <div className="overline text-primary mb-2">CHỈ SỐ TÀI CHÍNH</div>
          <h1 className="font-heading text-4xl font-black tracking-tight text-secondary">Bảng chỉ số MRR · GMV · Hoa hồng</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Hai chỉ số quyết định giá trị công ty. MRR đến từ phí kho hàng tháng, GMV × hoa hồng đến từ đơn hàng thực tế đi qua kho Helsinki.
          </p>
        </div>

        {/* Metrics cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <MetricCard icon={Repeat} tone="primary" label="MRR (định kỳ / tháng)" value={metrics ? fmt(metrics.mrr) : "—"} sub={metrics ? `${metrics.active_deals} hợp đồng đang chạy` : ""} tid="metric-mrr" />
          <MetricCard icon={Zap} tone="secondary" label="ARR (định kỳ / năm)" value={metrics ? fmt(metrics.arr) : "—"} sub="= MRR × 12" tid="metric-arr" />
          <MetricCard icon={TrendingUp} tone="primary" label="GMV (đơn hàng đã thanh toán)" value={metrics ? fmt(metrics.gmv_total) : "—"} sub={metrics ? `${metrics.transaction_counts.escrows + metrics.transaction_counts.sample_orders + metrics.transaction_counts.sample_program} giao dịch` : ""} tid="metric-gmv" />
          <MetricCard icon={Package} tone="secondary" label={`Hoa hồng ước tính · ${metrics?.weighted_commission_rate || 6}%`} value={metrics ? fmt(metrics.commission_earned) : "—"} sub="GMV × tỷ lệ trung bình" tid="metric-commission" />
        </div>

        {/* Turnkey offer counter */}
        {offer && (
          <Card data-testid="offer-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Ưu đãi Trọn gói · €3,000 credit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <Label className="text-xs">Tổng suất</Label>
                  <Input type="number" min={0} value={offer.cap} onChange={(e) => patchOffer({ cap: Number(e.target.value) })} data-testid="offer-cap" />
                </div>
                <div>
                  <Label className="text-xs">Đã claim</Label>
                  <Input type="number" min={0} value={offer.claimed} onChange={(e) => patchOffer({ claimed: Number(e.target.value) })} data-testid="offer-claimed" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Còn lại</div>
                  <div className="font-heading text-3xl font-black text-primary" data-testid="offer-remaining">{offer.remaining}</div>
                </div>
                <div>
                  <Button variant={offer.active ? "outline" : "default"} className="w-full" onClick={() => patchOffer({ active: !offer.active })} data-testid="offer-toggle">
                    {offer.active ? "Đang bật · tắt ngay" : "Đang tắt · bật lại"}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Khi số còn lại = 0, banner &ldquo;ƯU ĐÃI KHỞI ĐỘNG&rdquo; trên trang <code>/packages</code> tự chuyển sang trạng thái &ldquo;Đã đủ suất&rdquo;.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Deals table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Hợp đồng thương mại · {deals.length}</CardTitle>
            <Button onClick={() => setEditing(emptyDeal)} data-testid="deal-new">
              <Plus className="w-4 h-4 mr-1" /> Thêm hợp đồng
            </Button>
          </CardHeader>
          <CardContent>
            {deals.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground" data-testid="deals-empty">
                Chưa có hợp đồng nào. Thêm hợp đồng đầu tiên để bắt đầu theo dõi MRR.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="pb-3 font-semibold">Khách</th>
                      <th className="pb-3 font-semibold">Gói</th>
                      <th className="pb-3 font-semibold text-right">Setup</th>
                      <th className="pb-3 font-semibold text-right">MRR</th>
                      <th className="pb-3 font-semibold text-right">Hoa hồng</th>
                      <th className="pb-3 font-semibold">Trạng thái</th>
                      <th className="pb-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {deals.map((d) => (
                      <tr key={d.id} data-testid={`deal-row-${d.id}`}>
                        <td className="py-3">
                          <div className="font-semibold text-secondary">{d.client_name}</div>
                          <div className="text-xs text-muted-foreground">{d.company || "—"}</div>
                        </td>
                        <td className="py-3"><Badge variant="outline">{PACKAGE_LABELS[d.package] || d.package}</Badge></td>
                        <td className="py-3 text-right">{fmt(d.setup_fee)}</td>
                        <td className="py-3 text-right font-semibold text-primary">{fmt(d.monthly_fee)}/th</td>
                        <td className="py-3 text-right">{d.commission_rate}%</td>
                        <td className="py-3">
                          <Badge className={d.status === "active" ? "bg-green-100 text-green-800 border-transparent" : d.status === "paused" ? "bg-amber-100 text-amber-800 border-transparent" : "bg-red-100 text-red-800 border-transparent"}>
                            {STATUS_LABELS[d.status] || d.status}
                          </Badge>
                        </td>
                        <td className="py-3 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => setEditing(d)} data-testid={`deal-edit-${d.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteDeal(d.id)} data-testid={`deal-delete-${d.id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* GMV breakdown */}
        {metrics && (
          <Card>
            <CardHeader><CardTitle>Chi tiết GMV theo nguồn</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <BreakdownRow label="Escrow đơn hàng" amount={metrics.gmv_breakdown.escrows} count={metrics.transaction_counts.escrows} />
                <BreakdownRow label="Sample orders" amount={metrics.gmv_breakdown.sample_orders} count={metrics.transaction_counts.sample_orders} />
                <BreakdownRow label="Chương trình gửi mẫu" amount={metrics.gmv_breakdown.sample_program} count={metrics.transaction_counts.sample_program} />
              </div>
              <div className="mt-6 text-xs text-muted-foreground">
                Tổng phí setup đã thu (lifetime): <span className="font-semibold text-secondary">{fmt(metrics.setup_fees_lifetime)}</span>
                {" · "}Trong đó Trọn gói đang chạy: <span className="font-semibold text-secondary">{metrics.turnkey_active}</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="deal-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Chỉnh hợp đồng" : "Thêm hợp đồng thương mại"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Tên khách hàng *</Label><Input value={editing.client_name} onChange={(e) => setEditing({ ...editing, client_name: e.target.value })} data-testid="deal-name" /></div>
              <div><Label>Doanh nghiệp</Label><Input value={editing.company || ""} onChange={(e) => setEditing({ ...editing, company: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={editing.client_email || ""} onChange={(e) => setEditing({ ...editing, client_email: e.target.value })} /></div>
              <div>
                <Label>Gói *</Label>
                <select value={editing.package} onChange={(e) => setEditing({ ...editing, package: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="deal-package">
                  <option value="incorporate">Thành lập (€2,500)</option>
                  <option value="setup">Thiết lập & tuân thủ (€5,500)</option>
                  <option value="turnkey">Trọn gói (€9,500)</option>
                </select>
              </div>
              <div><Label>Phí setup (€)</Label><Input type="number" value={editing.setup_fee} onChange={(e) => setEditing({ ...editing, setup_fee: e.target.value })} data-testid="deal-setup" /></div>
              <div><Label>Phí tháng (€)</Label><Input type="number" value={editing.monthly_fee} onChange={(e) => setEditing({ ...editing, monthly_fee: e.target.value })} data-testid="deal-mrr" /></div>
              <div><Label>Hoa hồng (%)</Label><Input type="number" step="0.5" value={editing.commission_rate} onChange={(e) => setEditing({ ...editing, commission_rate: e.target.value })} data-testid="deal-commission" /></div>
              <div>
                <Label>Trạng thái</Label>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="active">Đang chạy</option>
                  <option value="paused">Tạm dừng</option>
                  <option value="churned">Đã nghỉ</option>
                </select>
              </div>
              <div className="md:col-span-2"><Label>Ghi chú</Label><Input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Huỷ</Button>
            <Button onClick={saveDeal} disabled={busy} data-testid="deal-save">{busy ? "Đang lưu…" : "Lưu"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function MetricCard({ icon: Icon, label, value, sub, tone, tid }) {
  const dark = tone === "secondary";
  return (
    <div data-testid={tid} className={`rounded-lg border p-6 card-lift ${dark ? "bg-secondary text-secondary-foreground border-transparent" : "bg-white border-border"}`}>
      <Icon className={`w-6 h-6 ${dark ? "text-primary" : "text-primary"} mb-4`} />
      <div className={`text-xs uppercase tracking-widest ${dark ? "text-secondary-foreground/60" : "text-muted-foreground"}`}>{label}</div>
      <div className={`mt-2 font-heading text-3xl font-black ${dark ? "text-primary" : "text-secondary"}`}>{value}</div>
      {sub && <div className={`mt-1 text-xs ${dark ? "text-secondary-foreground/60" : "text-muted-foreground"}`}>{sub}</div>}
    </div>
  );
}

function BreakdownRow({ label, amount, count }) {
  return (
    <div className="rounded-md border border-border p-4">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 font-heading text-2xl font-black text-secondary">{fmt(amount)}</div>
      <div className="text-xs text-muted-foreground mt-1">{count} giao dịch</div>
    </div>
  );
}
